import { useId, useState } from 'react'
import { Overlay } from '../../platform/overlay/Overlay'
import { Button } from '../../platform/primitives/Button'
import { Checkbox } from '../../platform/primitives/Checkbox'
import { ApplyFromField } from './ApplyFromField'
import { applyFailed, CONTINUE_FAILED, count, stoppedContinue } from './applyCopy'
import { localToday, longDate, windowStart } from './backfillWindow'
import { useApplyPreview, useApplyRule, type Applied, type ApplyPreview } from './useRuleApply'
import { RulesError } from './useRules'
import './rules.css'
import './rulePage.css'

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many)

/**
 * "M match the rule. K are already right." then what happens to the values the user set: left alone,
 * or, once they asked to change them, counted among the changes. Never "left alone" for a value the
 * apply will in fact write.
 */
export function breakdown(p: ApplyPreview, overwrite: boolean): string {
  const lead = `${count(p.matched)} match the rule. ${count(p.alreadyRight)} ${plural(p.alreadyRight, 'is', 'are')} already right.`
  if (overwrite) {
    return p.overwriting === 0
      ? lead
      : `${lead} ${count(p.overwriting)} ${plural(p.overwriting, 'has', 'have')} a value you set, and will change too.`
  }
  return `${lead} ${count(p.youSet)} ${plural(p.youSet, 'has', 'have')} a value you set, so ${plural(p.youSet, 'it is', 'they’re')} left alone.`
}

/**
 * Apply now (plan 2026-10-15, Task 4): run one saved rule over a window of past transactions, as one
 * batch the toast can undo.
 *
 * - **From** is always explicit and shown; the default is 90 days ago, read "Jul 14, 2026 · last
 *   90 days".
 * - The count is the server's dry run for exactly that window and choice, with the breakdown
 *   beneath it. 0 to change: the button reads "Nothing to change" and is `aria-disabled`.
 * - A window past the 5,000 cap (or the server's time budget) is never "at least": the apply walks
 *   the same newest rows and stops where the dry run did. A run cut short reports itself (its toast
 *   and Undo) and the dialog stays up: "Stopped after N. Continue to check older transactions."
 *   **Continue** re-sends the same apply (same From, same overwrite choice, both then fixed) with the
 *   server's `resume_after`, counted first by the dry run with the same cursor. It repeats until a run
 *   is not `truncated`, which closes the dialog. Each run is its own batch with its own Undo
 *   (backend 521).
 * - A cursor the server refuses (422) is never retried: "Couldn't continue. Start again from the
 *   beginning." A refused apply drops the cursor at once; a refused count offers **Start again**.
 * - Fields the user set are left alone unless "Also change ones I set" is ticked
 *   (`overwrite_existing`). Ticked, hand edits are changed too (`respect_user_edits: false`; owner
 *   ruling 2026-10-15), and the breakdown counts them as changing.
 *
 * `--accent` fills the apply button only, the one primary action here.
 */
export function ApplyNowDialog({
  ruleId,
  ruleName,
  onClose,
  onApplied,
}: {
  ruleId: string
  ruleName: string
  onClose: () => void
  /** Every run, the moment it lands: the caller reports it (the toast and its Undo). The dialog closes
   *  itself once a run is not cut short. */
  onApplied: (result: Applied) => void
}) {
  const today = localToday()
  const defaultFrom = windowStart(null, today)
  const [from, setFrom] = useState(defaultFrom)
  const [overwrite, setOverwrite] = useState(false)
  /** Set once a run is cut short: the next run continues from `after`, and From and overwrite are
   *  fixed. `reached` is what that run reached, for the status line. */
  const [resume, setResume] = useState<{ after: string; reached: number } | null>(null)
  const continuing = resume !== null
  const preview = useApplyPreview(ruleId, from, overwrite, resume?.after ?? null, true)
  const apply = useApplyRule(ruleId)
  const [failed, setFailed] = useState<string | null>(null)
  const countFailedId = useId()
  const applying = apply.isPending

  const p = preview.data
  const counting = preview.isFetching || p === undefined
  const n = p?.count ?? 0
  // A cursor the dry run refused: counting again from it would only be refused again.
  const cursorRefused = continuing && preview.isError && preview.error instanceof RulesError && preview.error.status === 422
  const title =
    p === undefined
      ? 'Apply this rule now'
      : `${count(n)} ${plural(n, 'transaction', 'transactions')} will change`

  const guard = (f: () => void) => () => {
    if (!applying) f()
  }

  // A stretch cut short is never "nothing": older transactions beyond it may still change.
  const nothing = !counting && !preview.isError && n === 0 && p?.truncated !== true

  const run = () => {
    if (applying || counting || nothing || preview.isError) return
    setFailed(null)
    apply.mutate(
      { kind: 'window', from, overwrite, resumeAfter: resume?.after ?? null },
      {
        onSuccess: (result) => {
          onApplied(result)
          if (result.truncated && result.resumeAfter !== null) setResume({ after: result.resumeAfter, reached: result.processed })
          else onClose()
        },
        onError: (e) => {
          const status = e instanceof RulesError ? e.status : undefined
          if (continuing && status === 422) {
            setResume(null)
            setFailed(CONTINUE_FAILED)
          } else setFailed(applyFailed(status))
        },
      },
    )
  }

  // A failed count is not still counting (rv-architect M3): the button says what it would do, and is
  // blocked with the alert as its reason.
  const label = applying
    ? 'Applying…'
    : preview.isError
      ? continuing
        ? 'Continue'
        : 'Apply'
      : counting
        ? 'Counting…'
        : nothing
          ? 'Nothing to change'
          : continuing
            ? 'Continue'
            : n === 0
              ? 'Check older transactions'
              : `Apply to ${count(n)}`
  const blocked = applying || counting || nothing || preview.isError

  return (
    <Overlay open onClose={guard(onClose)} role="dialog" label={`Apply “${ruleName}” now`}>
      <div className="rule-confirm">
        <h2 className="rule-confirm__title">{title}</h2>
        {/* Fixed while continuing: the next run covers the rest of THIS window. */}
        <ApplyFromField value={from} onChange={setFrom} readOnly={continuing || applying} />
        {/* Present from the start so the count is announced when it arrives. */}
        <p className="rule-confirm__body" role="status">
          {preview.isError
            ? ''
            : p === undefined
              ? 'Counting…'
              : breakdown(p, overwrite)}
        </p>
        {/* Mounted from the start, so a run cut short is announced. */}
        <p className="rule-confirm__status" role="status">
          {resume === null ? '' : stoppedContinue(resume.reached)}
        </p>
        {p?.truncated === true && !preview.isError && (
          <p className="rule-confirm__facts">
            {`It stops after the newest ${count(p.reached)} ${plural(p.reached, 'transaction', 'transactions')} since ${longDate(from)}. You can then continue to older ones.`}
          </p>
        )}
        {preview.isError && (
          <div className="rule-apply__count-failed" role="alert" id={countFailedId}>
            {cursorRefused ? (
              <>
                <p className="rule-confirm__error">{CONTINUE_FAILED}</p>
                <Button variant="outline" size="panel" onClick={() => setResume(null)}>
                  Start again
                </Button>
              </>
            ) : (
              <>
                <p className="rule-confirm__error">Couldn't count the transactions this would change.</p>
                <Button variant="outline" size="panel" onClick={() => void preview.refetch()}>
                  Try again
                </Button>
              </>
            )}
          </div>
        )}
        <label className="rule-apply__overwrite">
          <Checkbox checked={overwrite} onChange={(v) => !applying && !continuing && setOverwrite(v)} label="Also change ones I set" />
          <span aria-hidden="true">Also change ones I set</span>
        </label>
        <p className="rule-confirm__facts">You can undo the whole run right after.</p>
        {failed !== null && (
          <p className="rule-confirm__error" role="alert">
            {failed}
          </p>
        )}
        <div className="rule-confirm__actions">
          <Button variant="outline" size="page" aria-disabled={applying || undefined} onClick={guard(onClose)}>
            {continuing ? 'Done' : 'Cancel'}
          </Button>
          <Button
            variant="primary"
            size="page"
            aria-disabled={blocked || undefined}
            aria-busy={applying || undefined}
            aria-describedby={preview.isError && !applying ? countFailedId : undefined}
            onClick={run}
          >
            {label}
          </Button>
        </div>
      </div>
    </Overlay>
  )
}
